import {Injectable} from '@angular/core';
import {AnnouncementCategory} from '@app/models/enums/announcement-category';
import {GtManageOrganizationService} from '@app/services/gt-manage-organization.service';
import {mapResponse} from '@app/shared/utils/app-utils';
import {MediaAdministrationService, MediaFile} from '@gt-api';
import {BehaviorSubject, EMPTY, Subject} from 'rxjs';
import {catchError, distinctUntilChanged, filter, map, startWith, switchMap, tap} from 'rxjs/operators';

export interface MediaFileStoreModel {
  id: string;
  fileName: string;
  displayName: string;
  description: string;
  category: AnnouncementCategory;
  length: number;
  uploadTime: Date;
}

export interface DownloadLocation {
  fileName: string;
  downloadPath: string;
}

export interface MediaFileState {
  mediaFiles: MediaFileStoreModel[];
  playableLocation: DownloadLocation;
}

@Injectable({
  providedIn: 'root'
})
export class MediaFileStoreService {
  private state = new BehaviorSubject<MediaFileState>(null);
  private reloadTrigger = new Subject();

  mediaFiles$ = this.state.pipe(
    filter(state => state != null),
    map(state => state.mediaFiles)
  );
  downloadLocation$ = this.state.pipe(
    filter(state => state != null),
    map(state => state.playableLocation),
    distinctUntilChanged()
  );

  constructor(
    private manageOrganizationService: GtManageOrganizationService,
    private mediaAdministrationService: MediaAdministrationService
  ) {
    this.reloadTrigger
      .pipe(
        startWith(void 0 as void),
        switchMap(_ => this.manageOrganizationService.organization$.pipe(distinctUntilChanged((a, b) => a.id === b.id))),
        switchMap(_ => this.loadMediaFiles())
      )
      .subscribe();
  }

  reload() {
    this.reloadTrigger.next();
  }

  updatePlayableLocation(playableLocation: DownloadLocation) {
    this.state.next({...this.state.value, playableLocation});
  }

  private loadMediaFiles() {
    return this.mediaAdministrationService.getOrganizationMediaFiles1(this.manageOrganizationService.manageOname).pipe(
      mapResponse(response => response.mediaFiles),
      map(mediaFiles => mediaFiles.map(mf => this.mapToMediaFileStoreModel(mf))),
      catchError(error => {
        console.log('Unable to load media files', error);
        return EMPTY;
      }),
      tap(mediaFiles => this.state.next({...this.state.value, mediaFiles}))
    );
  }

  private mapToMediaFileStoreModel(mediaFile: MediaFile): MediaFileStoreModel {
    const model = {
      id: mediaFile.fileName,
      fileName: mediaFile.fileName,
      category: mediaFile.category || 200,
      displayName: mediaFile.displayName,
      description: mediaFile.description,
      length: mediaFile.length,
      uploadTime: new Date(mediaFile.uploadTime)
    } as MediaFileStoreModel;

    if (model.category === 200 && !model.fileName.startsWith('../' + this.manageOrganizationService.manageOname)) {
      model.fileName = '../' + this.manageOrganizationService.manageOname + '/' + model.fileName;
    }

    // Fallbacks to support old database storage model where description was used as display name
    if (!model.displayName || model.displayName.match('opptak_[0-9]{14}')) {
      model.displayName =
        model.description ||
        model.fileName
          .split('/')
          .pop()
          .replace(/\.[^/.]+$/, ''); // Gets filename without extension
    }
    return model;
  }
}
